import React, { useEffect, useRef, useState } from "react";
import Zoom from "@mui/material/Zoom";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import classes from "./MUIToolTip.module.css";
import Sources from "./Sources";
import api from "../../../BrandOnBoarding/api";
import MUIButton from "../MUIButton/MUIButton";
import Spinner from "../../Spinner/Spinner";
import LoadingSkeleton from "./LoadingSkeleton";
import { Download } from "@mui/icons-material";
import { downloadExcel } from "../../../utils/monitoring-helpers";

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#353945",
    color: "#FCFCFD",
    boxShadow: theme.shadows[1],
    fontSize: 11,
    maxWidth: 500,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: "#353945",
  },
}));

const getTruncatedText = (summary) => {
  return summary
  // const words = summary?.split(" ");
  // return words?.slice(0, 30).join(" ") + (words?.length > 50 ? "...." : "");
};

export default function MUIToolTip({
  sources = {},
  actionText = "",
  children,
  title,
  metric,
  metricScore,
  addToActionHandler,
  attribute,
  selectedInput,
  targetKey,
  isActionLoading,
  realtimeSources,
  isBrandOverview = false,
  rating,
  sourceData,
  sourceURLIds,
  isDiscovery = false,
  isBrandAudit = false,
  ...props
}) {
  const llmSources =
    sources && Object.values(sources)?.[0]
      ? Object.keys(Object.values(sources)[0])
      : [];
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState([]);
  const [realTimesource, setRealTimeSource] = useState([]);
  const abortCtrlRef = useRef();
  useEffect(() => {
    if (realtimeSources) {
      if (!open) {
        if (abortCtrlRef.current) {
          abortCtrlRef.current.abort();
        }
        setSource([]);
        return;
      }

      var timer = setTimeout(() => {
        getSources();
      }, 0);
    } else {
      setSource(llmSources);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [open]);

  async function getSources() {
    abortCtrlRef.current = new AbortController();
    let sources = llmSources;
    try {
      setLoading(true);
      const metricName = metric;
      const response = await realtimeSources(attribute, metricName);
      if (response.length > 0) {
        setRealTimeSource(response);
        sources = response;
      }
    } catch (error) {
    } finally {
      setSource(sources);
      setLoading(false);
    }
  }

  function handleTooltipOpen() {
    setOpen(true);
  }

  function handleTooltipClose() {
    setOpen(false);
  }

  function transformSourceDataToExport() {
    const input = Object.values(sources || {})?.[0] || {};
    if (!input || typeof input !== "object") return {};
    const result = {};
    Object.keys(input).forEach(key => {
      const aliases = input[key]?.aliases || [];

      result[key] = aliases.map(alias => ({
        http_status: alias.http_status,
        url: alias.source_alias_url
      }));
    });

    return result;
  }

  function ToolTipContent({ actionText, sourceURLIds }) {
    const entity = selectedInput?.brand
      ? `brand ${selectedInput?.brand}`
      : `product ${selectedInput?.product}`;
    const actionStatement = `The ${metric}-${attribute} score for the ${entity} is ${metricScore}`;

    return (
      <div className={classes.content}>
        <h6>{typeof title === "string" ? getTruncatedText(title) : actionStatement}</h6>
        <div className="d-flex justify-content-between align-items-center">
          <h6>Data Sources {`${realTimesource?.length > 0 ? ('(Attribute Level)') : ''}`}</h6>
          <Download
            style={{ cursor: 'pointer' }}
            onClick={() => {
              const exportData =
                realTimesource?.length > 0
                  ? realTimesource
                  : transformSourceDataToExport();

              const metricName = realTimesource?.length > 0 ? `${metric}-${attribute}` : metric;

              downloadExcel(exportData, metricName, selectedInput);
            }}
          />
        </div>


        {realtimeSources && loading && <LoadingSkeleton />}
        {source?.length > 0 && <Sources fullSourceData={sources} sourceURLIds={sourceURLIds} sources={source} selectedInput={selectedInput} />}
        {!isDiscovery &&
          <div className={classes.action_btn_wrapper}>
            {isActionLoading ? (
              <Spinner />
            ) : (
              <MUIButton
                size="small"
                className={classes.action_btn}
                variant="outlined"
                onClick={() => {
                  if (isBrandAudit) {
                    addToActionHandler(actionStatement, title, source, selectedInput?.brand, selectedInput?.llm);
                  } else if (isBrandOverview) {
                    addToActionHandler(actionStatement, title, realTimesource);
                  } else {
                    addToActionHandler(actionStatement, title, sources, targetKey, realTimesource);
                  }
                }}
              >
                {actionText}
              </MUIButton>
            )}
          </div>
        }
      </div>
    );
  }

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <div className={classes.tooltip_wrapper}>
        <LightTooltip
          open={open}
          onOpen={handleTooltipOpen}
          onClose={handleTooltipClose}
          slots={{
            transition: Zoom,
          }}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          onClick={() => setOpen(true)}
          title={<ToolTipContent sourceURLIds={sourceURLIds} actionText={actionText} />}
          {...props}
        >
          {children}
        </LightTooltip>
      </div>
    </ClickAwayListener>
  );
}
